/*
 * CredentialingQueuePanel — dedicated page for drivers awaiting VA clearance.
 * Reads /api/tpt/drivers/pending-credentialing. One-click Approve / Reject.
 * Reject requires a reason (stored on the driver row + surfaced in TP portal).
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, ShieldCheck, RefreshCw } from "lucide-react";
import { apiClient } from "../../lib/api";
import { confirm } from "../../components/ConfirmModal";

export default function CredentialingQueuePanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/tpt/drivers/pending-credentialing");
      setRows(data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load queue"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const approve = async (d) => {
    const ok = await confirm({
      title: `Clear ${d.name}?`,
      body: `This marks the driver as broker-compliant. They will become assignable on trips. TP: ${d.tp_name || "—"}.`,
      confirmLabel: "Clear driver",
    });
    if (!ok) return;
    setBusy(d.id);
    try {
      await apiClient.patch(`/tpt/drivers/${d.id}/credentialing`, { medexova_credentialing_status: "cleared" });
      toast.success(`${d.name} cleared`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(null); }
  };

  const retryMedexOva = async (d) => {
    setBusy(d.id);
    try {
      const { data } = await apiClient.post(`/medexova/drivers/${d.id}/submit`);
      toast.success(`Resubmitted to MedexOva · ${data?.status || "pending"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    } finally { setBusy(null); }
  };

  const submitReject = async () => {    if (!reason.trim()) { toast.error("Reason required"); return; }
    setBusy(rejectFor.id);
    try {
      await apiClient.patch(`/tpt/drivers/${rejectFor.id}/credentialing`, {
        medexova_credentialing_status: "rejected",
        medexova_rejection_reasons: [reason.trim()],
      });
      toast.info("Driver rejected — TP notified");
      setRejectFor(null); setReason("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="credentialing-panel">
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheck className="w-8 h-8 text-forest"/>
        <div>
          <h2 className="text-xl font-bold text-slate_navy">Credentialing queue</h2>
          <p className="text-sm text-slate-500">Drivers submitted by TPs awaiting broker-compliance review. Only cleared drivers can be assigned to trips.</p>
        </div>
      </div>

      {loading && <div className="text-center text-slate-500 py-12"><Loader2 className="w-5 h-5 animate-spin inline"/> Loading queue…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-center py-16 text-slate-500 bg-white border border-dashed border-[#E2E0D8] rounded-xl" data-testid="credentialing-empty">
          <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2"/>
          <div className="font-semibold">Queue is clean — no drivers pending.</div>
          <div className="text-xs mt-1">Newly added drivers by TPs will appear here for your review.</div>
        </div>
      )}
      {!loading && rows.length > 0 && (
        <div className="bg-white border border-[#E2E0D8] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Driver</th>
                <th className="text-left px-3 py-3">TP</th>
                <th className="text-left px-3 py-3">Vehicle</th>
                <th className="text-left px-3 py-3">Submitted</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1EFE8]">
              {rows.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50" data-testid={`cred-row-${d.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate_navy">{d.name}</div>
                    <div className="text-[11px] text-slate-500">{d.phone} {d.email ? ` · ${d.email}` : ""}</div>
                    {d.medexova_application_id && (
                      <div className="text-[10px] text-slate-400 font-mono mt-0.5" title="MedexOva application ID (OccuScreen)">
                        MedexOva: {d.medexova_application_id.slice(0, 12)}…
                      </div>
                    )}
                    {d.medexova_last_event && (
                      <div className="text-[10px] text-blue-600 mt-0.5">
                        Last MedexOva event: <b>{d.medexova_last_event}</b>
                      </div>
                    )}
                    {(d.medexova_rejection_reasons || []).length > 0 && (
                      <div className="mt-1 text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5" data-testid={`cred-rejection-reason-${d.id}`}>
                        <b>Rejection:</b> {(d.medexova_rejection_reasons || []).join(" · ")}
                      </div>
                    )}
                    {d.medexova_error && (
                      <div className="mt-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" title={d.medexova_error}>
                        <b>MedexOva error:</b> {d.medexova_error.slice(0, 60)}…
                      </div>
                    )}
                    {d.credentialing_expiring_field && (
                      <div className="mt-1 text-[10px] text-orange-800 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5" data-testid={`cred-expiry-warn-${d.id}`} title="MedexOva flagged this document as expiring soon">
                        <b>Expiring:</b> {d.credentialing_expiring_field} in {d.credentialing_expiring_days || "?"} days ({d.credentialing_expires_on?.slice(0, 10)})
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-slate-700">{d.tp_name}</td>
                  <td className="px-3 py-3 text-slate-500 capitalize">{d.vehicle_type || "—"}</td>
                  <td className="px-3 py-3 text-slate-500">{d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => approve(d)} disabled={busy === d.id} className="inline-flex items-center gap-1 bg-forest text-white px-3 py-1 rounded-md text-xs font-semibold mr-1" data-testid={`cred-approve-${d.id}`}>
                      <CheckCircle2 size={12}/> Clear
                    </button>
                    <button onClick={() => retryMedexOva(d)} disabled={busy === d.id} className="inline-flex items-center gap-1 border border-[#E2E0D8] px-3 py-1 rounded-md text-xs font-semibold mr-1 hover:bg-slate-50" data-testid={`cred-retry-${d.id}`} title="Resubmit to MedexOva">
                      <RefreshCw size={12}/> Retry
                    </button>
                    <button onClick={() => setRejectFor(d)} disabled={busy === d.id} className="inline-flex items-center gap-1 bg-red-100 text-red-700 border border-red-200 px-3 py-1 rounded-md text-xs font-semibold" data-testid={`cred-reject-${d.id}`}>
                      <XCircle size={12}/> Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rejectFor && (
        <div className="fixed inset-0 bg-black/60 z-[10000] grid place-items-center p-4" onClick={() => setRejectFor(null)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold text-slate_navy mb-2">Reject {rejectFor.name}?</div>
            <div className="text-xs text-slate-500 mb-3">This reason is stored on the driver record and shown to the TP dispatcher who submitted them.</div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="e.g. Missing valid CDL. Please re-submit with a copy of the CDL and updated MVR."
              className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm mb-3"
              data-testid="cred-reject-reason"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRejectFor(null)} className="px-4 py-2 rounded-md border border-[#E2E0D8] text-sm">Cancel</button>
              <button onClick={submitReject} disabled={busy || !reason.trim()} className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-semibold" data-testid="cred-reject-submit">Reject driver</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
